import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import './CreateVoucher.css';
import { IonAlert, IonButton, IonCol, IonGrid, IonIcon, IonInput, IonItem, IonLabel, IonList, IonModal, IonNote, IonPicker, IonRow, IonSelect, IonSelectOption, IonTextarea, IonToast, useIonAlert } from '@ionic/react';
import { getFarmIdsOfUser, getPlotIdsOfFarm } from '../services/farm.service';
import { useLocation } from 'react-router-dom';
import { authorizePayment, createComplimentaryOrder, createOrder, getPlans, getPlotPlan, getRecharges, getVouchers, updatePlotPlan } from '../services/payment.service';
import { addCircle, removeCircle } from 'ionicons/icons';
import { handleErrors } from '../services/common.service';
import Vouchers from './Vouchers';
import UserList from '../pages/UserList';
import { useRecoilValue } from 'recoil';
import { userProfileState } from '../services/state.service';

const FarmPicker = {
    name: "FarmId",
    options: [] as any[]
};

const setFarmPicker = (farmIds: any[]) => {
    FarmPicker.options = [];
    farmIds.forEach(item => {
        FarmPicker.options.push({
            text: item.farmId,
            value: item.farmId
        })
    });
};

const PlotPicker = {
    name: "PlotId",
    options: [] as any[]
};

const setPlotPicker = (plotIds: any[]) => {
    PlotPicker.options = [];
    plotIds.forEach(item => {
        PlotPicker.options.push({
            text: item.plotId,
            value: item.plotId
        })
    });
};

// Normal = today's paid flow (order at full price, then authorize). Complimentary = admin waives
// 100% of the price; the backend records a ₹0 payment and a ₹0 Zoho invoice at list price.
// There is deliberately no default: the admin must pick one for every submission.
const BILLING_NORMAL = 'NORMAL';
const BILLING_COMPLIMENTARY = 'COMPLIMENTARY';

const COMPLIMENTARY_REASONS = [
    { value: 'WARRANTY_REPLACEMENT', label: 'Warranty / replacement' },
    { value: 'DOWNTIME_COMPENSATION', label: 'Downtime compensation' },
    { value: 'PILOT_DEMO', label: 'Pilot / demo' },
    { value: 'GOODWILL', label: 'Goodwill' },
    { value: 'INTERNAL_TEST', label: 'Internal / test' },
    { value: 'OTHER', label: 'Other' },
];

const CreateVoucher = () => {
    const [present] = useIonAlert();
    const location = useLocation();
    const userProfile: any = useRecoilValue(userProfileState);
    const isAdmin = userProfile?.roles?.some(
        (role: any) => role.name === "admin"
    );

    const formik = useFormik({
        initialValues: {
            farmUserId: '',
            farmId: '',
            plotId: '',
            planId: '',
            vouchers: [{
                rechargeId: '',
                count: 1
            }],
            billingMode: '',
            waiverReason: '',
            waiverNote: ''
        },
        validationSchema: Yup.object({
            farmUserId: Yup.string().trim().required('Required'),
            farmId: Yup.string().trim().required('Required'),
            plotId: Yup.string().trim().required('Required'),
            planId: Yup.string().trim().required('Required'),
            vouchers: Yup.array().of(
                Yup.object().shape({
                    rechargeId: Yup.string().trim().required('Required')
                })),
            billingMode: Yup.string().oneOf([BILLING_NORMAL, BILLING_COMPLIMENTARY]).required('Choose Normal or Complimentary'),
            waiverReason: Yup.string().when('billingMode', {
                is: BILLING_COMPLIMENTARY,
                then: (schema: any) => schema.required('Required'),
            }),
            waiverNote: Yup.string().trim().when('billingMode', {
                is: BILLING_COMPLIMENTARY,
                then: (schema: any) => schema.min(3, 'At least 3 characters').max(500).required('Required'),
            })
        }),
        onSubmit: values => {
            setShowConfirmation(true);
        },
    });

    const [showConfirmation, setShowConfirmation] = useState(false);
    // True from "Yes" until the request settles (or the page reloads) — stops a second tap from
    // issuing a second order while the first is in flight.
    const [submitting, setSubmitting] = useState(false);
    const [prefilled, setPrefilled] = useState(false);
    const [onSuccess, setOnSuccess] = useState('');
    const [onError, setOnError] = useState('');

    useEffect(() => {
        const farmUserId = (location.state as any)?.farmUserId ?? '';
        if (farmUserId) {
            formik.setFieldValue('farmUserId', (location.state as any)?.farmUserId ?? '');
            formik.setFieldValue('farmId', (location.state as any)?.farmId ?? '');
            formik.setFieldValue('plotId', (location.state as any)?.plotId ?? '');
            setPrefilled(true);
        }
    }, [location]);

    const [farmPickerIsOpen, setFarmPickerIsOpen] = useState(false);
    useEffect(() => {
        let mounted = true;
        if (formik.values.farmUserId) {
            getFarmIdsOfUser(formik.values.farmUserId)
                .then(items => {
                    if (mounted) {
                        setFarmPicker(items)
                    }
                })
        }
        return () => { mounted = false };
    }, [formik.values.farmUserId])

    const [plotPickerIsOpen, setPlotPickerIsOpen] = useState(false);
    useEffect(() => {
        let mounted = true;
        if (formik.values.farmId) {
            getPlotIdsOfFarm(formik.values.farmId)
                .then(items => {
                    if (mounted) {
                        setPlotPicker(items)
                    }
                })
        }
        return () => { mounted = false };
    }, [formik.values.farmId])

    const [plans, setPlans] = useState([] as any[]);
    useEffect(() => {
        let mounted = true;
        getPlans()
            .then(items => {
                if (mounted) {
                    setPlans(items);
                }
            })
        return () => { mounted = false };
    }, [])

    const [recharges, setRecharges] = useState([] as any[]);
    useEffect(() => {
        let mounted = true;
        if (formik.values.planId) {
            getRecharges(formik.values.planId)
                .then(items => {
                    if (mounted) {
                        setRecharges(items);
                        resetVouchers();
                    }
                })
        }
        return () => { mounted = false };
    }, [formik.values.planId])

    const [currentPlotPlan, setCurrentPlotPlan] = useState();
    useEffect(() => {
        let mounted = true;
        if (formik.values.plotId) {
            getPlotPlan(formik.values.plotId)
                .then((data: any) => {
                    if (mounted) {
                        if (data.plotId) {
                            setCurrentPlotPlan(data);
                            formik.setFieldValue(`planId`, data.planId);
                        } else {
                            setCurrentPlotPlan(undefined);
                            formik.setFieldValue(`planId`, '');
                        }
                    }
                })
        }
        return () => { mounted = false };
    }, [formik.values.plotId])

    // A billing choice belongs to one user + plot + plan; clear it whenever any of them changes
    // so it's never silently carried over to a different plot.
    useEffect(() => {
        formik.setFieldValue('billingMode', '');
        formik.setFieldValue('waiverReason', '');
        formik.setFieldValue('waiverNote', '');
    }, [formik.values.farmUserId, formik.values.plotId, formik.values.planId])

    const isComplimentary = formik.values.billingMode === BILLING_COMPLIMENTARY;
    const voucherCount = formik.values.vouchers.reduce((sum, item) => sum + (item.rechargeId ? item.count : 0), 0);
    const listPrice = formik.values.vouchers.reduce((sum, item) => {
        const recharge = recharges.find((r: any) => r.id === item.rechargeId);
        return sum + (recharge?.price ?? 0) * item.count;
    }, 0);
    const reasonLabel = COMPLIMENTARY_REASONS.find(r => r.value === formik.values.waiverReason)?.label ?? '';

    const [vouchers, setVouchers] = useState([]);
    useEffect(() => {
        let mounted = true;
        if (formik.values.plotId) {
            getVouchers(formik.values.plotId)
                .then((data: any) => {
                    if (mounted) {
                        setVouchers(data);
                    }
                })
        }
        return () => { mounted = false };
    }, [formik.values.plotId])

    const removeVoucherHandler = (voucher: any) => {
        const index = vouchers.findIndex((item: any) => item.id === voucher.id);
        vouchers.splice(index, 1);
        setVouchers([...vouchers]);
    }

    const [showSelectUserModal, setShowSelectUserModal] = useState(false);

    function setFarmUserId(item: any) {
        formik.setFieldValue('farmUserId', item.farmUserId);
        formik.setFieldValue('farmId', '');
        formik.setFieldValue('plotId', '');
        setVouchers([]);
        setShowSelectUserModal(false);
    }

    function updateVoucherCount(index: number, value: number) {
        formik.setFieldValue(`vouchers[${index}].count`, formik.values.vouchers[index].count + value);
    }

    function addVoucher() {
        formik.values.vouchers.push({
            rechargeId: '',
            count: 1
        });
        formik.setFieldValue(`vouchers`, formik.values.vouchers);
    }

    function removeVoucher() {
        formik.values.vouchers.pop();
        formik.setFieldValue(`vouchers`, formik.values.vouchers);
    }

    function resetVouchers() {
        formik.values.vouchers = [{
            rechargeId: '',
            count: 1
        }];
        formik.setFieldValue(`vouchers`, formik.values.vouchers);
    }

    const savePlotPlan = async (values: any) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const plotPlanData = {
                plotId: formik.values.plotId,
                userId: formik.values.farmUserId,
                planId: formik.values.planId
            }
            const response = await updatePlotPlan(plotPlanData, currentPlotPlan).then(handleErrors);
            if (response) {
                setCurrentPlotPlan(response);
            }

            const newVouchers = [] as any[];
            formik.values.vouchers.forEach(item => {
                for (let i = 0; i < item.count; i++) {
                    newVouchers.push({
                        plotId: formik.values.plotId,
                        rechargeId: item.rechargeId
                    });
                }
            });
            if (formik.values.billingMode === BILLING_COMPLIMENTARY) {
                // Already confirmed in the dialog: one call creates the ₹0 order and its vouchers.
                const created = await createComplimentaryOrder(
                    formik.values.farmUserId,
                    newVouchers,
                    formik.values.waiverReason,
                    formik.values.waiverNote.trim()
                ).then(handleErrors);
                const warnings: string[] = created?.warnings ?? [];
                if (warnings.length) {
                    // Vouchers exist, but something needs follow-up (e.g. no Zoho item) — make sure it's read.
                    present({
                        header: 'Created, with warnings',
                        message: warnings.join('\n\n'),
                        buttons: ['OK'],
                        onDidDismiss: () => window.location.reload(),
                    });
                    return;
                }
                setOnSuccess(`Created ${newVouchers.length} complimentary voucher(s) at ₹0`);
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
                return;
            }

            const createdOrder = await createOrder(formik.values.farmUserId, newVouchers).then(handleErrors);
            present({
                header: 'Authorize',
                message: `Authorize payment of ${createdOrder.currency} ${createdOrder.amount}`,
                buttons: [
                    { text: 'Cancel', role: 'cancel', handler: () => setSubmitting(false) },
                    {
                        text: 'Ok', role: 'confirm', handler: async (d) => {
                            try {
                                // the endpoint answers 200 with `false` when voucher creation fails
                                const authorized = await authorizePayment(createdOrder.id).then(handleErrors);
                                if (authorized !== true) {
                                    throw Error('Authorization failed — check the plot\'s vouchers before retrying');
                                }
                            } catch (err: any) {
                                setSubmitting(false);
                                setOnError(err?.message ?? String(err));
                                return;
                            }
                            const plotId = formik.values.plotId;
                            formik.setFieldValue(`plotId`, '');
                            setTimeout(() => {
                                formik.setFieldValue(`plotId`, plotId);
                            }, 100);
                            setOnSuccess(`Successfully created ${newVouchers.length} vouchers`);
                            setTimeout(() => {
                                window.location.reload();
                            }, 1000);
                        }
                    },
                ],
                // Cancel or a tap outside the dialog: nothing was authorized, re-enable the form.
                onDidDismiss: (e) => { if (e.detail.role !== 'confirm') setSubmitting(false); },
            });
        } catch (err: any) {
            setSubmitting(false);
            setOnError(err?.message ?? String(err));
        }
    };

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <IonList>
                    <IonItem>
                        <IonLabel position="stacked">User Id</IonLabel>
                        <IonInput readonly disabled={prefilled} id="farmUserId" name="farmUserId" value={formik.values.farmUserId} placeholder="Select User Id" onClick={e => setShowSelectUserModal(true)}></IonInput>
                    </IonItem>
                    {formik.touched.farmUserId && formik.errors.farmUserId ? (<div className="errorMsg">{formik.errors.farmUserId}</div>) : null}

                    <IonItem>
                        <IonLabel position="stacked">Farm Id</IonLabel>
                        <IonInput readonly disabled={prefilled || !formik.values.farmUserId} id="farmId" name="farmId" value={formik.values.farmId} placeholder="Select Farm Id" onClick={e => setFarmPickerIsOpen(true)}></IonInput>
                    </IonItem>
                    {formik.touched.farmId && formik.errors.farmId ? (<div className="errorMsg">{formik.errors.farmId}</div>) : null}

                    <IonItem>
                        <IonLabel position="stacked">Plot Id</IonLabel>
                        <IonInput readonly disabled={prefilled || !formik.values.farmId} id="plotId" name="plotId" value={formik.values.plotId} placeholder="Plot Id" onClick={e => setPlotPickerIsOpen(true)}></IonInput>
                    </IonItem>
                    {formik.touched.plotId && formik.errors.plotId ? (<div className="errorMsg">{formik.errors.plotId}</div>) : null}

                    <IonItem>
                        <IonLabel position="stacked">Plan</IonLabel>
                        <IonSelect id="planId" name="planId" value={formik.values.planId} placeholder="Select Plan" onIonChange={formik.handleChange}>
                            {plans && plans.map(item => {
                                return <IonSelectOption key={item.id} value={item.id}>{item.name}</IonSelectOption>;
                            })}
                        </IonSelect>
                    </IonItem>
                    {formik.touched.planId && formik.errors.planId ? (<div className="errorMsg">{formik.errors.planId}</div>) : null}

                    <IonItem lines="none">
                        <IonLabel position="floating">Voucher(s)</IonLabel>
                        <IonGrid fixed={true} class="ion-no-padding">
                            {formik.values.vouchers.map((item, index) => {
                                return <React.Fragment key={index}>
                                    <IonRow class="ion-no-padding">
                                        <IonCol size="6" class="ion-no-padding">
                                            <IonSelect className="recharge-select" id={`vouchers[${index}].rechargeId`} name={`vouchers[${index}].rechargeId`} disabled={!formik.values.planId} value={formik.values.vouchers[index].rechargeId} placeholder="Select Recharge" onIonChange={formik.handleChange}>
                                                {recharges && recharges.map(item => {
                                                    return <IonSelectOption key={item.id} value={item.id}>{item.name}</IonSelectOption>;
                                                })}
                                            </IonSelect>
                                        </IonCol>
                                        <IonCol size="2" class="ion-no-padding ion-text-center">
                                            <IonButton fill="clear" size="small" disabled={formik.values.vouchers[index].count === 1} onClick={e => updateVoucherCount(index, -1)}><IonIcon slot="icon-only" icon={removeCircle} /></IonButton>
                                        </IonCol>
                                        <IonCol size="2" class="ion-no-padding ion-text-center">
                                            <IonButton fill="clear" size="small">{formik.values.vouchers[index].count}</IonButton>
                                        </IonCol>
                                        <IonCol size="2" class="ion-no-padding ion-text-center">
                                            <IonButton fill="clear" size="small" onClick={e => updateVoucherCount(index, 1)}><IonIcon slot="icon-only" icon={addCircle} /></IonButton>
                                        </IonCol>
                                    </IonRow>
                                    {formik.touched.vouchers && formik.touched.vouchers[index] && formik.touched.vouchers[index].rechargeId && formik.errors.vouchers && formik.errors.vouchers[index] && (formik.errors.vouchers[index] as any).rechargeId ? (<div className="errorMsg">{(formik.errors.vouchers[index] as any).rechargeId}</div>) : null}
                                </React.Fragment>
                            })}
                        </IonGrid>
                    </IonItem>
                    <IonGrid>
                        <IonRow>
                            <IonCol class="ion-text-center">
                                <IonButton fill="clear" onClick={e => addVoucher()}>Add Voucher</IonButton>
                            </IonCol>
                            <IonCol class="ion-text-center">
                                <IonButton fill="clear" disabled={formik.values.vouchers.length === 1} onClick={e => removeVoucher()}>Delete Voucher</IonButton>
                            </IonCol>
                        </IonRow>
                    </IonGrid>
                </IonList>

                {isAdmin && <>
                    <IonItem>
                        <IonLabel position="stacked">Billing</IonLabel>
                        <IonSelect id="billingMode" name="billingMode" value={formik.values.billingMode} placeholder="Choose Normal or Complimentary" onIonChange={formik.handleChange}>
                            <IonSelectOption value={BILLING_NORMAL}>Normal (paid / authorize)</IonSelectOption>
                            <IonSelectOption value={BILLING_COMPLIMENTARY}>Complimentary (100% waived, ₹0)</IonSelectOption>
                        </IonSelect>
                    </IonItem>
                    {formik.touched.billingMode && formik.errors.billingMode ? (<div className="errorMsg">{formik.errors.billingMode}</div>) : null}

                    {isComplimentary && <>
                        <IonItem lines="none">
                            <IonNote color="warning">
                                The farmer is not charged. Recorded as a ₹0 payment with a ₹0 Zoho invoice at the full list price{listPrice ? ` (₹${listPrice})` : ''}.
                            </IonNote>
                        </IonItem>
                        <IonItem>
                            <IonLabel position="stacked">Reason</IonLabel>
                            <IonSelect id="waiverReason" name="waiverReason" value={formik.values.waiverReason} placeholder="Select Reason" onIonChange={formik.handleChange}>
                                {COMPLIMENTARY_REASONS.map(item => <IonSelectOption key={item.value} value={item.value}>{item.label}</IonSelectOption>)}
                            </IonSelect>
                        </IonItem>
                        {formik.touched.waiverReason && formik.errors.waiverReason ? (<div className="errorMsg">{formik.errors.waiverReason}</div>) : null}

                        <IonItem>
                            <IonLabel position="stacked">Note</IonLabel>
                            <IonTextarea id="waiverNote" name="waiverNote" value={formik.values.waiverNote} placeholder="Why is this complimentary? (ticket no., approval, etc.)" autoGrow onIonChange={formik.handleChange} onIonBlur={() => formik.setFieldTouched('waiverNote')}></IonTextarea>
                        </IonItem>
                        {formik.touched.waiverNote && formik.errors.waiverNote ? (<div className="errorMsg">{formik.errors.waiverNote}</div>) : null}
                    </>}

                    <IonButton expand="block" type="submit" disabled={!formik.values.billingMode || submitting}>
                        {submitting ? 'Creating…' : isComplimentary ? 'Create Complimentary Vouchers' : 'Create Vouchers'}
                    </IonButton>
                </>}
            </form>

            {vouchers && <Vouchers vouchers={vouchers} removeVoucherHandler={removeVoucherHandler} />}

            {showSelectUserModal && <IonModal isOpen={showSelectUserModal} >
            <UserList
                    selectedValue={formik.values.farmUserId}
                    onClickHandler={setFarmUserId}
                    onClose={setShowSelectUserModal}
                />
            </IonModal>}

            <IonPicker
                isOpen={farmPickerIsOpen}
                columns={[FarmPicker]}
                buttons={[
                    {
                        text: "Cancel",
                        role: "cancel",
                        handler: value => {
                            setFarmPickerIsOpen(false);
                        }
                    },
                    {
                        text: "Confirm",
                        handler: value => {
                            formik.values.farmId = value.FarmId.value;
                            formik.values.plotId = '';
                            setVouchers([]);
                            setFarmPickerIsOpen(false);
                        }
                    }
                ]}
            ></IonPicker>

            <IonPicker
                isOpen={plotPickerIsOpen}
                columns={[PlotPicker]}
                buttons={[
                    {
                        text: "Cancel",
                        role: "cancel",
                        handler: value => {
                            setPlotPickerIsOpen(false);
                        }
                    },
                    {
                        text: "Confirm",
                        handler: value => {
                            formik.values.plotId = value.PlotId.value;
                            setPlotPickerIsOpen(false);
                        }
                    }
                ]}
            ></IonPicker>

            <IonAlert
                isOpen={showConfirmation}
                onDidDismiss={() => setShowConfirmation(false)}
                header={isComplimentary ? 'Confirm complimentary vouchers' : 'Confirm!'}
                message={isComplimentary
                    ? `Create ${voucherCount} voucher(s) worth ₹${listPrice} for plot ${formik.values.plotId} at ₹0? Reason: ${reasonLabel}. This skips payment and is recorded as complimentary.`
                    : 'Please make sure that you have verified all the plot and plan details. Create Vouchers?'}
                buttons={[
                    {
                        text: 'No',
                        role: 'cancel',
                        cssClass: 'secondary'
                    },
                    {
                        text: 'Yes',
                        handler: () => {
                            savePlotPlan(formik.values);
                        }
                    }
                ]}
            />

            <IonToast
                isOpen={!!onSuccess}
                onDidDismiss={() => setOnSuccess('')}
                message={onSuccess}
                duration={2000}
                color="success"
            />

            <IonToast
                isOpen={!!onError}
                onDidDismiss={() => setOnError('')}
                message={onError}
                duration={2000}
                color="danger"
            />
        </>
    );
};

export default CreateVoucher;